// ── CardFilters ───────────────────────────────────────────────────────────────
// Renders: search-bar + count row, busking tabs, category/subcategory filters,
// and talent-filter bubbles.  Each section only mounts when relevant.

import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { CategoriesValue } from "@/shared";
import AllTalentFilterOption from "./deck-library/AllTalentFilterOption";
import { DeckCardType, TalentItem } from "../types";
import Categories from "./Categories";
import SubCategories from "./SubCategories";

interface CardFiltersProps {
  darkMode: boolean;
  loading: boolean;
  cardType: DeckCardType;
  filteredCount: number;
  // Search
  searchQuery: string;
  onSearchChange: (q: string) => void;
  // Busking tabs
  isBusking?: boolean;
  buskingTab?: number;
  onBuskingTabChange?: (tab: number) => void;
  hasCompletedBusking?: boolean;
  isUserView: boolean;
  // Category / subcategory (Taas_Card)
  categoryData: CategoriesValue[];
  selectedCategory: string | null;
  selectedSubCategories: string[];
  onCategoryClick: (slug: string) => void;
  onSubCategoryClick: (slug: string) => void;
  uniqueTalents: TalentItem[];
  selectedTalentId: number | string | null;
  onTalentSelect: (id: number | string | null) => void;
}

const CardFilters: React.FC<CardFiltersProps> = ({
  darkMode,
  loading,
  cardType,
  filteredCount,
  searchQuery,
  onSearchChange,
  isBusking,
  buskingTab,
  onBuskingTabChange,
  hasCompletedBusking,
  isUserView,
  categoryData,
  selectedCategory,
  selectedSubCategories,
  onCategoryClick,
  onSubCategoryClick,
  uniqueTalents,
  selectedTalentId,
  onTalentSelect,
}) => {
  const { t } = useTranslation();

  const showTalentFilter =
    !loading &&
    uniqueTalents.length > 0 &&
    (cardType === "Single_Card" ||
      cardType === "Demo_Card" ||
      cardType === "Listening_Together_Card" ||
      cardType === "Season_Card");

  return (
    <>
      {/* ── Search + card-count row ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: '"Lexend", sans-serif',
            fontWeight: 700,
            fontSize: { xs: "1rem", sm: "1.125rem" },
            color: darkMode ? "var(--neutralWhite)" : "var(--neutral-900)",
          }}
        >
          {loading
            ? "Loading…"
            : `${filteredCount} ${filteredCount === 1 ? "card" : "cards"}`}
        </Typography>

        <TextField
          placeholder={t("search_cards")}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          sx={{
            width: { xs: "100%", sm: 280, md: 320 },
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: darkMode ? "var(--neutral-800)" : "var(--neutralWhite)",
              border: darkMode
                ? "1px solid var(--neutral-700)"
                : "1px solid var(--neutral-200)",
              "& fieldset": { border: "none" },
              "&:hover": {
                border: darkMode
                  ? "1px solid var(--neutral-600)"
                  : "1px solid var(--neutral-300)",
              },
              "&.Mui-focused": {
                border: darkMode
                  ? "1px solid var(--primary-500)"
                  : "1px solid var(--primary-600)",
              },
            },
            "& .MuiInputBase-input": {
              color: darkMode ? "var(--neutralWhite)" : "var(--neutral-900)",
              fontSize: "0.875rem",
              py: 1.25,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    color: darkMode
                      ? "var(--neutral-400)"
                      : "var(--neutral-500)",
                    fontSize: "1.125rem",
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* ── Busking sub-tabs ── */}
      {isBusking && !loading && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            backgroundColor: darkMode ? "var(--neutral-900)" : "#fff",
            border: `1px solid ${darkMode ? "var(--neutral-800)" : "#E5E7EB"}`,
            borderRadius: 2,
          }}
        >
          <Tabs
            value={buskingTab}
            onChange={(_e, v) => onBuskingTabChange?.(v)}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontFamily: '"Lexend", sans-serif',
                fontWeight: 600,
                color: darkMode ? "var(--neutral-400)" : "#6B7280",
                "&.Mui-selected": {
                  color: darkMode ? "var(--primary-400)" : "var(--primary-600)",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: darkMode
                  ? "var(--primary-400)"
                  : "var(--primary-600)",
              },
            }}
          >
            <Tab
              label={t("live")}
              value={0}
              sx={{ minWidth: { xs: 100, sm: 150 } }}
            />
            <Tab
              label={t("upcoming")}
              value={1}
              sx={{ minWidth: { xs: 100, sm: 150 } }}
            />
            {!isUserView && hasCompletedBusking && (
              <Tab
                label={t("completed")}
                value={2}
                sx={{ minWidth: { xs: 100, sm: 150 } }}
              />
            )}
          </Tabs>
        </Paper>
      )}

      {/* ── Talent filter deck variants ── */}
      {showTalentFilter && (
        <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              gap: { xs: 1.5, sm: 2 },
              p: 2,
              pl: 1,
              alignItems: "flex-start",
              overflowX: "auto",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {/* "All" bubble */}
            <AllTalentFilterOption
              selected={selectedTalentId === null}
              darkMode={darkMode}
              cardType={cardType}
              onClick={() => onTalentSelect(null)}
            />

            {/* Individual talent bubbles */}
            {uniqueTalents.map((talent) => (
              <Box
                key={talent.id}
                onClick={() =>
                  onTalentSelect(
                    selectedTalentId === talent.id ? null : talent.id
                  )
                }
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                  gap: 0.75,
                  opacity:
                    selectedTalentId === talent.id || selectedTalentId === null
                      ? 1
                      : 0.6,
                  transition: "opacity 0.2s",
                  "&:hover": { opacity: 1 },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 64, sm: 74 },
                    height: { xs: 64, sm: 74 },
                    borderRadius: "50%",
                    border: `2px solid ${
                      selectedTalentId === talent.id
                        ? darkMode
                          ? "var(--primary-400)"
                          : "var(--primary-600)"
                        : darkMode
                          ? "var(--neutral-700)"
                          : "var(--neutral-300)"
                    }`,
                    overflow: "hidden",
                    backgroundColor: darkMode
                      ? "var(--neutral-800)"
                      : "var(--neutral-100)",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {talent.profile_image ? (
                    <Box
                      component="img"
                      src={talent.profile_image}
                      alt={talent.username}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                        fontWeight: 600,
                        color: darkMode
                          ? "var(--neutral-300)"
                          : "var(--neutral-700)",
                      }}
                    >
                      {talent.username.charAt(0).toUpperCase()}
                    </Typography>
                  )}
                </Box>

                <Typography
                  sx={{
                    fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                    color: darkMode
                      ? "var(--neutral-400)"
                      : "var(--neutral-600)",
                    textAlign: "center",
                    maxWidth: { xs: 56, sm: 64 },
                    textWrap: "wrap",
                    textOverflow: "ellipsis",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {talent.username}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </>
  );
};

export default CardFilters;
